import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { lmLogo, passwordEyeIcon } from '../assets/superAdminAssets';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormBuilder, Validators, FieldControl, FieldGroup } from 'react-reactive-form';
import { emailValidator } from '../helpers/validator.helper';
import { useAppContext } from '../contexts/AppContext';
import { markAllControlsDirtyAndTouched } from '../helpers/form.helper';
import uuid from 'react-uuid';
import { CookieNames, setCookie, getCookie } from '../helpers/cookie.helper';
import { Constants } from '../constants';
import { useUser } from '../store/hooks/useUser';
import { login, logout, ReSendVerificationCodeEmail, verifyRecaptcha } from '../services/login.service';

const LoginFormContent = React.memo(({ loginForm, onSubmit, showPassword, setShowPassword, recaptchaRefV2, recaptchaRefV3, sitekeyV3, sitekeyV2, showChallenge, onRecaptchaV2Change }) => (
  <form onSubmit={onSubmit}>
    <FieldControl
      strict={false}
      render={({ handler, touched, hasError }) => (
        <div className={`form-group ${touched && (hasError('required') || hasError('invalidEmail')) ? 'error-field' : ''}`}>
          <label htmlFor="email">
            Email address
            <span className="asterisk">*</span>
          </label>
          <input
            {...handler()}
            type="email"
            className="form-control"
            id="email"
            aria-describedby="emailHelp"
            placeholder="Enter email"
          />
          {touched && hasError('required') && (
            <span className="error-message">
              Email is required
            </span>
          )}
          {touched && hasError('invalidEmail') && (
            <span className="error-message">
              Please enter a valid email address
            </span>
          )}
        </div>
      )}
      control={loginForm.get('email')}
    />

    <FieldControl
      strict={false}
      render={({ handler, touched, hasError }) => (
        <div className={`form-group input-password ${touched && hasError('required') ? 'error-field' : ''}`}>
          <label htmlFor="password">
            Password
            <span className="asterisk">*</span>
          </label>
          <input
            {...handler()}
            type={showPassword ? 'text' : 'password'}
            className="form-control"
            id="password"
            placeholder="Password"
          />
          <img className={`pass_eye ${showPassword ? 'active_pass' : ''}`} id="pass_eye" src={passwordEyeIcon} alt="" onClick={() => setShowPassword(!showPassword)} />
          {touched && hasError('required') && (
            <span className="error-message">
              Password is required
            </span>
          )}
        </div>
      )}
      control={loginForm.get('password')}
    />

    <button type="submit" className="btn btn-login w-100">
      Log In
    </button>

    {/* reCAPTCHA v3 (invisible) */}
    <ReCAPTCHA
      ref={recaptchaRefV3}
      sitekey={sitekeyV3}
      size="invisible"
    />

    {/* reCAPTCHA v2 (visible challenge) - shown only when v3 score is low */}
    {showChallenge && (
      <div className="captcha-box w-100 mt-3">
        <ReCAPTCHA
          ref={recaptchaRefV2}
          sitekey={sitekeyV2}
          onChange={onRecaptchaV2Change}
        />
      </div>
    )}
  </form>
));

const Login = () => {
  const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();
  const { fetchUserProfile, updateUser } = useUser();
  const navigate = useNavigate();
  const [showActiveSession, setShowActiveSession] = useState(false);
  const [MASPopupShow, setShowMASPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // reCAPTCHA refs and state
  const recaptchaRefV3 = useRef(null);
  const recaptchaRefV2 = useRef(null);
  const [showChallenge, setShowChallenge] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [waitingForChallenge, setWaitingForChallenge] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);

  const sitekeyV3 = Constants.RECAPTCHA_SITE_KEY; // v3 (invisible)
  const sitekeyV2 = Constants.RECAPTCHA_V2_SITE_KEY; // v2 (visible challenge)
  const localHost = window.location.href.includes("localhost");

  const loginForm = useMemo(() => FormBuilder.group({
    email: ['', [Validators.required, emailValidator]],
    password: ['', Validators.required],
  }), []);

  useEffect(() => {
    document.title = 'Loan Mantra - Login';
    return () => {
      loginForm.reset();
    }
  }, []);

  const performLogin = useCallback(async (userData, hasChallenge = false, v2Token = null) => {
    try {
      // If v2 challenge was shown and token is available, add it to userData
      if (hasChallenge && v2Token) {
        userData.recaptchaTokenV2 = v2Token;
      }

      const response = await login(userData);

      if (response.message === "Two Factor Authentication Login") {
        showLoader();
        ReSendVerificationCodeEmail(response.resultObject)
          .then((res) => {
            showToaster(res.message, "success");
            setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
            hideLoader();
          })
        return navigate("/email-verification", { state: response.resultObject });
      } else if (response.resultObject && response.status !== 404 && response.message !== "Two Factor Authentication Login") {
        setShowChallenge(false);
        setRecaptchaToken(null);

        await fetchUserProfile();

        // Set CompanyId in cookie if available
        if (response.resultObject.companyId) {
          setCookie(CookieNames.COMPANY_ID, response.resultObject.companyId);
        }

        updateUser({ refreshTokenWarningAction: 'REFRESH', timeToLogout: Constants.SESSION_TIMEOUT, isLoggedIn: true });

        // Navigate to dashboard and replace history so user cannot go back to login
        navigate("/dashboard", { replace: true });

        setTimeout(() => {
          hideLoader();
        }, 0);
      } else {
        showToaster(response.message, "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        hideLoader();
      }
    } catch (err) {
      hideLoader();
      if (err.response?.data?.message === "This account is not verified. Please verify your email to proceed." || err.response?.data?.message === "Your account is temporarily locked.  Please try back in 30 minutes.  In a rush, please call a Loan Mantra advisor for help.") {
        showToaster(err.response.data.message, "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      } else if (err.response?.data?.message === "New Paasword link" && err.response?.data?.status === 404) {
        // Handle password reset link
        navigate(`/ResetPasswordLink?data=${encodeURIComponent(loginForm.value.email)}`);
      } else if (err.response?.status === 401 && err.response?.data?.message === "User already logged") {
        setShowActiveSession(true);
        setShowMASPopup(true);
      } else if (err.response?.data?.message === "Low reCAPTCHA score") {
        // Show v2 challenge when v3 score is low
        setShowChallenge(true);
        setRecaptchaToken(null);
        showToaster("Please complete the security challenge to proceed.", "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      } else if (err.response && (err.response.status === 401 || err.response.status === 404)) {
        showToaster(err.response?.data?.message || "Login failed", "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      } else {
        showToaster("Login failed. Please try again.", "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    }
  }, [showLoader, hideLoader, showToaster, hideToaster, navigate, fetchUserProfile, updateUser, loginForm.value.email]);

  // Watch for challenge completion - when v2 challenge is completed and token is set
  useEffect(() => {
    if (waitingForChallenge && recaptchaToken && pendingUserData) {
      setWaitingForChallenge(false);
      performLogin(pendingUserData, true, recaptchaToken);
      setPendingUserData(null);
    }
  }, [recaptchaToken, waitingForChallenge, pendingUserData, performLogin]);

  const loginUser = useCallback(async () => {
    markAllControlsDirtyAndTouched(loginForm);

    if (loginForm.status !== "INVALID") {
      showLoader();
      setCookie(CookieNames.SESSION_ID, uuid());

      const userData = {
        EmailAddress: loginForm.value.email,
        Password: loginForm.value.password,
        SessionId: getCookie(CookieNames.SESSION_ID),
      };

      localStorage.setItem("email", loginForm.value.email);

      try {
        if (Constants.isCheckCaptcha) {
          // Execute reCAPTCHA v3
          if (!recaptchaRefV3.current) {
            showToaster("reCAPTCHA not loaded. Please refresh the page.", "error");
            setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
            hideLoader();
            return;
          }

          const v3Token = await recaptchaRefV3.current.execute('login');
          userData.recaptchaToken = v3Token;

          // Verify v3 token score
          const verification = await verifyRecaptcha(v3Token, "login");

          if (!verification?.resultObject?.success || verification?.resultObject?.score < 0.3) {
            // Score is low, show v2 challenge
            setShowChallenge(true);
            setWaitingForChallenge(true);
            setPendingUserData(userData);
            setRecaptchaToken(null);
            showToaster("Please complete the security challenge to proceed.", "error");
            setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
            hideLoader();
            return;
          }
        }

        // Score is good, perform login
        await performLogin(userData, false, null);
      } catch (error) {
        hideLoader();
        showToaster("An error occurred during login. Please try again.", "error");
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } else {
      markAllControlsDirtyAndTouched(loginForm);
      hideLoader();
    }
  }, [loginForm, showLoader, hideLoader, showToaster, hideToaster, performLogin]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    loginUser();
  }, [loginUser]);

  const handleSession = async (e) => {
    e.preventDefault();
    try {
      showLoader();
      await logout(loginForm.value.email || localStorage.getItem('email'));
      loginUser();
    } catch (error) {
      hideLoader();
      showToaster("Something went wrong while logging out. Please try again.", "error");
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    }
  }

  return (
    <div className="website-wrap">
      <section className="login-body">
        <div className="text-center mb-5">
          <img src={lmLogo} width="120" alt="Loan Mantra" />
        </div>
        <div className="container text">
          <div className="login-box">
            {/* <h4 className="t4ext-center mb-5">Login as super admin</h4> */}
            <LoginFormContent
              loginForm={loginForm}
              onSubmit={handleSubmit}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              recaptchaRefV2={recaptchaRefV2}
              recaptchaRefV3={recaptchaRefV3}
              sitekeyV3={sitekeyV3}
              sitekeyV2={sitekeyV2}
              showChallenge={showChallenge}
              onRecaptchaV2Change={(token) => setRecaptchaToken(token)}
            />

            <div className="text-center mt-4">
              <Link className="link-secondary" to="/forgot-password">
                Forgot password?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Active Session PopUp */}
      {showActiveSession && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowActiveSession(false);
              setShowMASPopup(false);
            }}
          ></div>
          <div
            className="modal show d-block fade mas-popup"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="MASPopupLabel"
            aria-hidden="false"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                {/* adding svg code to avoid loading issue */}
                <svg className="mb-3" width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="28" cy="28" r="28" fill="#F0F2FF" />
                  <g clipPath="url(#clip0_2920_9759)">
                    <path d="M28 32.6V28.1M28.45 25.4C28.45 25.6485 28.2485 25.85 28 25.85C27.7515 25.85 27.55 25.6485 27.55 25.4M28.45 25.4C28.45 25.1515 28.2485 24.95 28 24.95C27.7515 24.95 27.55 25.1515 27.55 25.4M28.45 25.4H27.55M37 29C37 33.9706 32.9706 38 28 38C23.0294 38 19 33.9706 19 29C19 24.0294 23.0294 20 28 20C32.9706 20 37 24.0294 37 29Z" stroke="#22315D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2920_9759">
                      <rect width="20" height="20" fill="white" transform="translate(18 19)" />
                    </clipPath>
                  </defs>
                </svg>
                <h3 className="mt-2">Multiple Active Sessions</h3>
                <p>It seems that you have an active login session. Do you want to close out the prior session?</p>
                <div className="modal-footer justify-content-center border-0 mt-3">
                  <button className="back-btn" onClick={() => setShowActiveSession(false)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-login btn-confirm"
                    onClick={(e) => handleSession(e)}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;


